import dayjs from "dayjs";
import _ from "lodash";
import {useFormatNumber, formatMoneySmart} from "@/composables/useUtils"
import { buildProductSoldItemRows } from "~/utils/productSoldThermalRows";
import { useSettingsStore } from "~/stores/settings";
import { isMoneyMasked, MASKED_MONEY } from "@/composables/useDashboardMoneyMask";

interface ReportHeaderOptions {
    title: string;
    date: string;
    time: string;
    reportName: string;
    shift?: string;
    cashier?: string;
}
interface ReportFooterOptions {
    printAt: string;
    printBy: string;
}

// create class receipt builder
export default class PrintCustomNiceloopV1Builder {
    private job : any = null;

    // Hide-cashier: when the logged-in PIN has HideCashier set, every money figure
    // on the printed report (summary income + product-sold) is replaced with the
    // masked placeholder "x,xxx" — the same mask the on-screen dashboard uses.
    // Non-money labels/counts (bill counts, product names, times) stay visible.
    private money(value:any, digits:number = 2){
        return isMoneyMasked() ? MASKED_MONEY : (useFormatNumber(value, digits, true) || 0);
    }
    private moneySmart(value:any){
        return isMoneyMasked() ? MASKED_MONEY : formatMoneySmart(value);
    }

    // create method to build receipt
    build() {
        return this.job;
    }

    buildHeaderReport(headerOptions:ReportHeaderOptions){
        const header = [
            {
                "name": "PrintTitle",
                "value": headerOptions.title
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "Date",
                "value": headerOptions.date
            },
            {
                "name": "Time",
                "value": headerOptions.time
            },
            {
                "name": "Report",
                "value": headerOptions.reportName
            },
        ]

        if(headerOptions.shift){
            header.push(
                {
                    "name": "Shift",
                    "value": headerOptions.shift
                }
            )
        }

        if(headerOptions.cashier){
            header.push(
                {
                    "name": "Cashier",
                    "value": headerOptions.cashier
                }
            )
        }

        return header
    }

    buildFooterReport(footerOptions:ReportFooterOptions){
        const footer = [
           
            {
                "name": "PrintComment",
                "value": `Print by : ${footerOptions.printBy}`
            },
          
            {
                "name": "PrintComment",
                "value": `Print at : ${footerOptions.printAt}`
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintLine",
                "value": true
            },
            {
                "name": "LineFeed",
                "value": true
            }
        ]

        return footer
    }

    // Close Shop marker — only emitted on real close-shop prints (NOT interim
    // mid-day checks). Matches the legacy app: a centered/italic banner with the
    // close time at the very top and a closing banner at the very bottom, so a
    // printed report is unambiguously the shop-closing copy. Shared by every
    // report printed in the Close Shop flow (Summary Income, Product Sold, Void,
    // Open Menu) — keep all of them identical, don't inline per report.
    buildCloseShopHeader(isCloseShop:boolean, isChangeShift:boolean = false){
        if(!isCloseShop) return []
        // Same banner mechanics as Close Shop, but the Change Shift flow re-uses this
        // header — so swap the wording. Otherwise a shift-change report would be
        // mislabelled "Close Shop".
        const label = isChangeShift ? 'Change Shift' : 'Close Shop'
        return [
            {
                "name": "PrintComment",
                "value": `***** ${label} ${dayjs().format('HH:mm:ss  DD/MMMM/YYYY')} *****`
            },
            ...this.buildLineFeed(),
        ]
    }

    // Outstanding open-table balance carried into the next shift. Only printed when
    // the caller supplies it (Change Shift flow). `outstanding = { count, total }`.
    buildOutstandingData(outstanding:any){
        if(!outstanding) return []
        const count = Number(outstanding?.count) || 0
        const total = Number(outstanding?.total) || 0
        return [
            {
                "name": "PrintComment",
                "value": `ยอดโต๊ะเปิดค้าง ${count} โต๊ะ = ${this.money(total)}`
            },
            ...this.buildLineFeed(),
        ]
    }

    // Warning banner printed when the close-shop receipts refresh from the server
    // failed: the total is computed from this terminal's LOCAL data only and may
    // not include other cashiers' / 2nd-cashier sales.
    buildLocalOnlyWarning(isLocalOnly:boolean){
        if(!isLocalOnly) return []
        return [
            {
                "name": "PrintComment",
                "value": "!!! ดึงข้อมูลจาก Server ไม่ได้ - ยอดนี้เป็นข้อมูลเครื่องนี้เท่านั้น (LOCAL ONLY) อาจไม่รวมยอดเครื่องแคชเชียร์อื่น !!!"
            },
            ...this.buildLineFeed(),
        ]
    }

    buildCloseShopFooter(isCloseShop:boolean, isChangeShift:boolean = false){
        if(!isCloseShop) return []
        const label = isChangeShift ? 'Change Shift' : 'Close Shop'
        return [
            ...this.buildLineFeed(),
            {
                "name": "PrintComment",
                "value": `***** ${label} ${dayjs().format('HH:mm:ss  DD/MMMM/YYYY')} *****`
            },
        ]
    }

    makeSummaryIncomeReport(payload:any) {
        // Report period start = shop-open time (Close Shop) or first receipt time
        // (historical reports). Computed upstream in ThermalPrintSummaryIncome.
        // Falls back to "now"/"7:00" when no valid start time could be resolved —
        // printing must never fail just because the open time is missing.
        const startMoment = payload?.startTime && dayjs(payload.startTime).isValid()
            ? dayjs(payload.startTime)
            : null;
        const startTimeText = startMoment ? startMoment.format('HH:mm') : '7:00';
        const reportDate = startMoment ? startMoment : dayjs();
        const headerOptions = {
            title: payload.shopName,
            date: reportDate.format('DD/MMMM/YYYY'),
            time: `${startTimeText} - ${dayjs().format('HH:mm')}`,
            reportName: "Summary Income",
            shift: "All",
            cashier: "All"
        }

        const footerOptions = {
            printAt: dayjs().format('HH:mm:ss DD/MMMM/YYYY'),
            printBy: payload?.User || payload?.employee?.Name || "My Employee"
        }

        const closeShopHeader = this.buildCloseShopHeader(payload?.isCloseShop, payload?.isChangeShift)
        const closeShopFooter = this.buildCloseShopFooter(payload?.isCloseShop, payload?.isChangeShift)
        const localOnlyWarning = this.buildLocalOnlyWarning(payload?.isLocalOnly)
        const outstandingData = this.buildOutstandingData(payload?.outstanding)

        const header = this.buildHeaderReport(headerOptions)
        const footer = this.buildFooterReport(footerOptions)
        const shiftData = this.buildShiftData(payload.shiftsSummary)
        const drawerData = this.buildDrawerData(payload.drawerData)
        const paymentTypeData = this.buildPaymentTypeData(payload.paymentTypesSummary)
        const onTheHouseData = this.buildOnTheHouseData(payload.onTheHouseSummary || [])
        const cashierData = this.buildCashierData(payload.cashiersSummary)

        // Full Report ("Receipt Full Report" / incomeDetail): the summary/totals
        // block above is followed by a receipt-by-receipt list so staff can
        // reconcile each pin. Matches the legacy printRowOrders layout — good
        // bills under "Bills", then voided bills under "Void Bills".
        let billsSummaryData : any[] = []
        if(payload?.isFullReport){
            billsSummaryData = [
                ...this.buildBillSummary(payload.goodBills, 'Bills'),
                ...(payload.voidBills?.length ? this.buildBillSummary(payload.voidBills, 'Void Bills') : [])
            ]
        }
        

        this.job = [
            ...closeShopHeader,
            ...localOnlyWarning,
            ...header,
            {
                "name": "PrintLine",
                "value": true
            },
            ...this.buildLineFeed(),
            {
                "name": "PrintTitle",
                "value": `Summary (${payload.goodBills.length} bills)`
            },
            ...this.buildLineFeed(),
            // Guest count line only when Customer mode is enabled (hidden on the
            // close-shop / summary report otherwise).
            ...(useSettingsStore().enableCustomerMode
                ? [{ "name": "Customer : " + (payload.guests || 0), "value": "" }]
                : []),
            {
                "name": "Total",
                "value": this.money(payload.billing?.subTotal + Math.abs(payload.billing?.itemsDiscountTotal || 0))
            },
            {
                "name": "Discount Items",
                "value": this.money(payload.billing?.itemsDiscountTotal)
            },
            {
                "name": "Service",
                "value": this.money(payload.billing?.serviceChangeTotal)
            },
            {
                "name": "Discount Bill",
                "value": this.money(payload.billing?.discountBillTotal)
            },
            {
                "name": "CreditCard Charge",
                "value": this.money(payload.billing?.cardChargeTotal)
            },
            // VAT split: Exclusive (added on top, positive) and Inclusive (embedded,
            // shown as a deduction/negative). A mixed close-shop day has both, so each
            // non-zero side prints as its own line.
            ...(payload.billing?.vatExclusiveTotal
                ? [{ "name": "VAT (Exclusive)", "value": this.money(payload.billing?.vatExclusiveTotal) }]
                : []),
            ...(payload.billing?.vatInclusiveTotal
                ? [{ "name": "VAT (Inclusive)", "value": this.money(payload.billing?.vatInclusiveTotal) }]
                : []),
            {
                "name": "PrintLine",
                "value": true
            },
            {
                "name": "Grand Total",
                "value": `*** ${this.money(payload.billing?.grandTotal)}`
            },
            {
                "name": "PrintDoubleLine",
                "value": true
            },
            ...this.buildLineFeed(),
            {
                "name": "PrintComment",
                "value": `Voided ${payload.voidBills.length} bills, ${this.money(payload.billing?.voidedBillsAmount)}`
            },
            ...this.buildLineFeed(),
            ...outstandingData,
            ...drawerData,
            ...this.buildLineFeed(),
            ...shiftData,
            ...this.buildLineFeed(),
            ...paymentTypeData,
            ...this.buildLineFeed(),
            ...onTheHouseData,
            ...(onTheHouseData.length > 0 ? this.buildLineFeed() : []),
           ...cashierData,
           ...this.buildLineFeed(1),
            {
                "name": "PrintLine",
                "value": true
            },
            ...this.buildLineFeed(),
            ...billsSummaryData,
            ...footer,
            ...closeShopFooter
        ]

        return this
    }

    makeProductSoldReport(payload:any) {
        const headerOptions = {
            title: payload.shopName,
            date: dayjs().format('DD/MMMM/YYYY'),
            time: `7:00 - ${dayjs().format('HH:mm')}`,
            reportName: "Product Sold",
            shift: "All",
            cashier: "All"
        }

        const footerOptions = {
            printAt: dayjs().format('HH:mm:ss DD/MMMM/YYYY'),
            printBy: payload?.employee?.name || "Niceloop"
        }

        const closeShopHeader = this.buildCloseShopHeader(payload?.isCloseShop, payload?.isChangeShift)
        const closeShopFooter = this.buildCloseShopFooter(payload?.isCloseShop, payload?.isChangeShift)

        const header = this.buildHeaderReport(headerOptions)
        const footer = this.buildFooterReport(footerOptions)
        let producSoldData = []

        if(payload?.isFullReport){
            producSoldData = this.buildProductSoldSummary(payload.productSoldSummary)
        }


        this.job = [
            ...closeShopHeader,
            ...header,
            ...this.buildLineFeed(),
            {
                "name": "PrintLine",
                "value": true
            },
            ...this.buildLineFeed(),
            {
                "name": "PrintTitle",
                "value": "Products by type"
            },
            ...this.buildLineFeed(),
            ...this.buildProductTypeSummary(payload.groups),            
            ...this.buildLineFeed(),
            {
                "name": "PrintComment",
                "value": "ไม่รวม/หักส่วนลดท้ายบิล"
            },
            ...this.buildLineFeed(),
            {
                "name": "PrintLine",
                "value": true
            },
            ...this.buildLineFeed(),
            ...producSoldData,
            ...footer,
            ...closeShopFooter
        ]

        return this
    }

    makeVoidReport(payload:any) {
        const headerOptions = {
            title: payload.shopName,
            date: dayjs().format('DD/MMMM/YYYY'),
            time: `7:00 - ${dayjs().format('HH:mm')}`,
            reportName: "Void Items",
            shift: "All",
            cashier: "All"
        }

        const footerOptions = {
            printAt: dayjs().format('HH:mm:ss DD/MMMM/YYYY'),
            printBy: payload?.employee?.name || "Niceloop"
        }

        const closeShopHeader = this.buildCloseShopHeader(payload?.isCloseShop, payload?.isChangeShift)
        const closeShopFooter = this.buildCloseShopFooter(payload?.isCloseShop, payload?.isChangeShift)

        const header = this.buildHeaderReport(headerOptions)
        const footer = this.buildFooterReport(footerOptions)
        const buildedVoidItemsData = this.buildVoidItemsReport(payload.voidItemsList)

        this.job = [
            ...closeShopHeader,
            ...header,

            {
                "name": "PrintLine",
                "value": true
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintTitle",
                "value": "list void items"
            },
            {
                "name": "LineFeed",
                "value": true
            },
            ...buildedVoidItemsData,
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintLine",
                "value": true
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintTextLine",
                "value": `Void Amount : ${useFormatNumber(payload.voidAmount, 2, true)}`
            },
            ...footer,
            ...closeShopFooter
        ]

        return this
    }

    makeVoidReceipt(payload:any) {
        const voidedAt = dayjs(payload.voidedAt || undefined).format('HH:mm:ss DD/MMM/YYYY')
        this.job = [
            { name: "PrintTitle", value: "Deleted Bill (Voided)" },
            ...this.buildLineFeed(),
            { name: "Invoice Id", value: String(payload.invoiceId ?? "-") },
            { name: "Date Time", value: String(payload.dateTime ?? "-") },
            { name: "Table", value: String(payload.table ?? "-") },
            { name: "GrandTotal", value: useFormatNumber(payload.grandTotal, 2, true) },
            { name: "Payment", value: String(payload.paymentType ?? "-") },
            ...this.buildLineFeed(),
            { name: "Voided At", value: voidedAt },
            { name: "Employee", value: String(payload.employee ?? "-") },
            { name: "Reason", value: String(payload.reason ?? "") },
            ...this.buildLineFeed(2),
            { name: "PrintTextLine", value: "Sign : ______________________________________" },
            ...this.buildLineFeed(2),
        ]
        return this
    }

    // Cashier (printer 0) copy of an item-level void. Unlike the kitchen slips —
    // which are split per station by item tag — this lists EVERY voided item on
    // a single slip so the cashier has the full record of what was voided and why.
    // Like legacy, it carries no prices or totals — qty + name only — and opens
    // with the "VOID (ยกเลิก)" box so it can't be mistaken for an order slip.
    makeVoidItemsCashier(payload:any) {
        const voidedAt = dayjs(payload.voidedAt || undefined).format('HH:mm:ss DD/MMM/YYYY')
        const items = Array.isArray(payload.items) ? payload.items : []
        // Topping void: the item stays on the bill, only the topping is voided.
        const voidLabel = payload.toppingOnly ? "ยกเลิกเฉพาะ Topping" : "ยกเลิก"

        const itemRows = items.map((it:any) => ({
            name: "PrintTextLine",
            value: `${it.qty}  ${it.name} (${voidLabel})`,
        }))

        this.job = [
            { name: "PrintTitle", value: "ยกเลิกรายการ (VOID)" },
            ...this.buildLineFeed(),
            { name: "PrintTextLine", value: "************************" },
            { name: "PrintTextLine", value: `*****  VOID (${voidLabel})` },
            { name: "PrintTextLine", value: `***** Approved : ${payload.employee ?? "-"}` },
            { name: "PrintTextLine", value: "************************" },
            ...this.buildLineFeed(),
            { name: "Table", value: `${payload.alias ?? "-"} (ยกเลิก)` },
            { name: "Void By", value: String(payload.employee ?? "-") },
            { name: "Voided At", value: voidedAt },
            ...this.buildLineFeed(),
            { name: "PrintLine", value: true },
            ...this.buildLineFeed(),
            ...itemRows,
            ...this.buildLineFeed(),
            { name: "PrintLine", value: true },
            ...this.buildLineFeed(),
            { name: "PrintTextLine", value: `Reason :   ${payload.reason || '-'}` },
            ...this.buildLineFeed(2),
            { name: "PrintTextLine", value: "Sign : ______________________________________" },
            ...this.buildLineFeed(2),
        ]
        return this
    }

    makeOpenMenuReport(payload:any) {
        const headerOptions = {
            title: payload.shopName,
            date: dayjs().format('DD/MMMM/YYYY'),
            time: `7:00 - ${dayjs().format('HH:mm')}`,
            reportName: "Open Menu",    
        }

        const footerOptions = {
            printAt: 'Printed At : ' + dayjs().format('HH:mm:ss DD/MMMM/YYYY'),
            printBy: payload?.employee?.name || "Niceloop"
        }

        const closeShopHeader = this.buildCloseShopHeader(payload?.isCloseShop, payload?.isChangeShift)
        const closeShopFooter = this.buildCloseShopFooter(payload?.isCloseShop, payload?.isChangeShift)

        const header = this.buildHeaderReport(headerOptions)
        const footer = this.buildFooterReport(footerOptions)

        let buildContent = payload.openMenuData.map((item:any) => {
            return {
                "name": item.name,
                "value": this.money(item.extendedPrice)
            }
        })

        if(payload.openMenuData.length == 0){
            buildContent = [
                {
                    "name": "PrintTextLine",
                    "value": "ไม่มีรายการขาย Open Menu"
                },
            ]
        }

        this.job = [
            ...closeShopHeader,
            ...header,
            ...this.buildLineFeed(),
            {
                "name": "PrintLine",
                "value": true
            },
            ...this.buildLineFeed(),
            {
                "name": "PrintTitle",
                "value": "OPEN MENU"
            },
            ...this.buildLineFeed(),            
            {
                "name": "PrintComment",
                "value": "** ราคาที่แสดงเป็นราคาที่หักส่วนลดสินค้าแล้ว"
            },
            ...this.buildLineFeed(2),  
            ...buildContent,
            ...this.buildLineFeed(2),
            {
                "name": "PrintComment",
                "value": footerOptions.printAt
            },
            ...this.buildLineFeed(),
            {
                "name": "PrintLine",
                "value": true
            },
            ...this.buildLineFeed(),
            ...closeShopFooter
        ]

        return this
    }

    makePrinterTest(payload: { title?: string; lines?: string[] } = {}) {
        const title = payload.title || "PRINTER TEST"
        const lines = (payload.lines || []).filter(Boolean)

        this.job = [
            {
                "name": "PrintTitle",
                "value": title
            },
            ...this.buildLineFeed(),
            ...lines.map((line) => ({
                "name": "PrintTextLine",
                "value": line
            })),
            ...this.buildLineFeed(2),
            {
                "name": "PrintLine",
                "value": true
            },
            ...this.buildLineFeed(),
        ]

        return this
    }



    makeFullProductSoldReport(){
        const headerOptions = {
            title: "Niceloop_1",
            date: "24 September 2025",
            time: "7:00 - 15:32",
            reportName: "Void Items",
            shift: "All",
            cashier: "All"
        }

        const footerOptions = {
            printAt: "15:32:16 24 September 2025",
            printBy: "Niceloop"
        }

        const header = this.buildHeaderReport(headerOptions)
        const footer = this.buildFooterReport(footerOptions)
        
        this.job = [
           ...header,
         
            {
                "name": "PrintLine",
                "value": true
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintTitle",
                "value": "Products by type"
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "Food   (100%)",
                "value": "   1,000.00"
            },
            {
                "name": "Dessert   (0%)",
                "value": "   0.00"
            },
            {
                "name": "Other   (0%)",
                "value": "   0.00"
            },
            {
                "name": "Drink   (0%)",
                "value": "   0.00"
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintComment",
                "value": "ไม่รวม/หักส่วนลดท้ายบิล"
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintLine",
                "value": true
            },
         
            {
                "name": "PrintTitle",
                "value": "Detail"
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintComment",
                "value": "*** Food : อาหารไทย 1 :    350.00"
            },
            {
                "name": "1 กุ้งผัดพริกไทยดำ",
                "value": "   350.00"
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintComment",
                "value": "*** Food : ยำ 1  :    0.00"
            },
            {
                "name": "1 ยำมะเขือยาว",
                "value": "   0.00"
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "PrintComment",
                "value": "*** Food : อาหารทะเล :    650.00"
            },
            {
                "name": "1 ปูทะเลผัดผงกะหรี่ (1ตัว)",
                "value": "   650.00"
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "LineFeed",
                "value": true
            },
            ...footer
        ]

        return this
    }

    buildLineFeed(count:number = 1){
        return Array(count).fill({
            "name": "LineFeed",
            "value": true
        })
    }

    buildDrawerData(drawerData){
        return [
            {
                "name": "PrintTitle",
                "value": "Summary Cash Drawer"
            },
            {
                "name": "LineFeed",
                "value": true
            },
            {
                "name": "Opening Time",
                "value": drawerData.start?.timeText || ""
            },
            {
                "name": "Opening Cash",
                "value": this.money(drawerData.start.amount)
            },
            {
                "name": `Cash In (${drawerData.deposit.count})`,
                "value": this.money(drawerData.deposit.amount)
            },
            {
                "name": `Cash Out (${drawerData.withdraw.count})`,
                "value": this.money(drawerData.withdraw.amount)
            },
            {
                "name": `Cash Refunded (${drawerData.returned.count})`,
                "value": this.money(drawerData.returned.amount)
            },
            {
                "name": "Total Cash (compute from POS)",
                "value": this.money(drawerData.drawerTotal)
            },
            {
                "name": "Actual Cash (count from drawer) ",
                "value": this.money(drawerData.actualCash.amount)
            },
            {
                "name": "Total Diff ",
                "value": this.money(drawerData.diff)
            }
        ]
    }

    buildShiftData(shiftsSummary:any){
        return [
            {
                "name": "PrintTitle",
                "value": "By ShiftWork"
            },
            ...shiftsSummary.map((shift:any) => {
                return {
                    "name": `Shift ${shift.shift} (${shift.quantity})`,
                    "value": `   ${this.money(shift.total)}`
                }
            }),
        ]
    }

    buildPaymentTypeData(paymentTypesSummary:any){
   
        return [
            {
                "name": "PrintTitle",
                "value": "By payment types"
            },
            ...[...paymentTypesSummary]
                .sort((a: any, b: any) => Number(b?.total || 0) - Number(a?.total || 0))
                .map((paymentType:any) => {
                    return {
                        "name": `${paymentType.type} (${paymentType.quantity})`,
                        "value": `${this.moneySmart(paymentType.total)}`
                    }
                })
        ]
    }

    buildOnTheHouseData(onTheHouseSummary: any) {
        if (!Array.isArray(onTheHouseSummary) || onTheHouseSummary.length === 0) {
            return []
        }
        return [
            {
                "name": "PrintTitle",
                "value": "Entertain / OnTheHouse"
            },
            ...[...onTheHouseSummary]
                .sort((a: any, b: any) => Number(b?.total || 0) - Number(a?.total || 0))
                .map((row: any) => {
                    return {
                        "name": `${row.name} (${row.quantity})`,
                        "value": `${this.moneySmart(row.total)}`
                    }
                })
        ]
    }

    buildCashierData(cashiersSummary:any){
        return [
            {
                "name": "PrintTitle",
                "value": "By Cashier"
            },
            ...[...cashiersSummary]
                .sort((a: any, b: any) => Number(b?.total || 0) - Number(a?.total || 0))
                .map((cashier:any) => {
                    return {
                        "name": `${cashier.name} (${cashier.quantity})`,
                        "value": `${this.moneySmart(cashier.total)}`
                    }
                })
        ]
    }

    buildProductTypeSummary(productTypeSummary:any){
        return [
            ...productTypeSummary.map((productType:any) => {
                return {
                    "name": `${productType.name} (${productType.percent}%)`,
                    "value": `${this.moneySmart(productType.total)}`
                }
            })
        ]
    }

    buildBillSummary(receipts:any, title:string = 'Bills'){
        const displayPaymentTypes = (receipt:any)=>{
            if (!Array.isArray(receipt.multiplePayment)) {
                return receipt?.PaymentType || "-";
            }

            return receipt.multiplePayment.map((payment:any) => payment.name).join(", ");
        }

        const drawBillsContent = receipts.map((receipt:any, index:number) => {
            return [
                ...this.buildLineFeed(),
                {
                    "name": `${index + 1}. ${dayjs(receipt.Date).format('HH:mm:ss DD MMM')}`,
                    "value": `   ${this.money(receipt.GrandTotal)}`
                },
                {
                    "name": "PrintComment",
                    "value": `     #${receipt?.ReceiptId || receipt?.OrderId || "no-id"}, ${displayPaymentTypes(receipt)}, By : ${receipt.User}`
                },
            ]
        })

        const totalBill = receipts.reduce((total, receipt) => total + (receipt.GrandTotal || 0), 0);

        const content = [
            ...this.buildLineFeed(),
            {
                "name": "PrintTitle",
                "value": `${title} :    ${this.money(totalBill)}`
            },

            ..._.flatMap(drawBillsContent),    
            
            ...this.buildLineFeed(2),
            {
                "name": "PrintLine",
                "value": true
            },
            ...this.buildLineFeed(),
        ]

        return content
    }

    buildProductSoldSummary(productSoldSummary: any) {
        let drawProductSoldContent: any[] = [];

        for (const [typeName, typeData] of Object.entries(productSoldSummary ?? {})) {
            const catalogs = (typeData as any)?.items ?? [];
            if (!Array.isArray(catalogs) || catalogs.length === 0) continue;

            for (const catalog of catalogs) {
                const catalogName = catalog?.name ?? "-";
                const catalogTotal = Number(catalog?.totalamount ?? 0);

                // header (italic via PrintComment): *** Type : Catalog : total
                const content: any[] = [
                    ...this.buildLineFeed(),
                    {
                        name: "PrintComment",
                        value: `*** ${typeName} : ${catalogName} :    ${this.moneySmart(catalogTotal)}`,
                    },
                ];

                // Group subItems by their parent item so the shared formatter can
                // suppress "(subName)" only when there's a single Regular sub.
                const subs = Array.isArray(catalog?.subItems) ? catalog.subItems : [];
                const itemMap = new Map<string, { itemName: string; subItems: Array<{ subName: string; qty: number; amount: number }> }>();
                for (const sub of subs) {
                    const itemName = String(sub?.itemName || catalog?.name || "-");
                    const key = String(sub?.itemKey || itemName);
                    const bucket = itemMap.get(key) ?? { itemName, subItems: [] };
                    bucket.subItems.push({
                        subName: String(sub?.subName || sub?.name || ""),
                        qty: Number(sub?.quantity ?? 0),
                        amount: Number(sub?.amount ?? 0),
                    });
                    itemMap.set(key, bucket);
                }

                for (const item of itemMap.values()) {
                    const rows = buildProductSoldItemRows(item, (n) => this.moneySmart(n));
                    for (const row of rows) content.push(row);
                }

                drawProductSoldContent = [...drawProductSoldContent, ...content];
            }
        }

        return [
            ...this.buildLineFeed(),
            { name: "PrintTitle", value: "Detail" },
            ...drawProductSoldContent,
        ];
    }

    buildVoidItemsReport(items:any) {
        let content = []
        for(let item of items){
            content.push({
                name: "PrintTextLine",
                value: `${dayjs(item.timestamp).format('HH:mm')} ${item.name} x${item.quantity} `
            })
        }
        return content
    }







}
